import type { LinkedInStatus } from "../hooks/useLinkedInStatus";
import { useSystemConfig, useToggleForceDraftMode } from "../hooks/useSystemConfig";

interface Props {
  status?: LinkedInStatus;
}

const startUrl = () =>
  `${import.meta.env.VITE_API_URL ?? ""}/api/auth/linkedin/start`;

// Only trust https avatar URLs coming from scraped/external data.
const httpsUrl = (raw: string | null | undefined): string | null => {
  if (!raw) return null;
  try {
    const url = new URL(raw);
    return url.protocol === "https:" ? url.href : null;
  } catch {
    return null;
  }
};

export const LinkedInConnectionCard = ({ status }: Props) => {
  const connected = !!status?.connected;
  const { data: cfg } = useSystemConfig();
  const toggle = useToggleForceDraftMode();
  const avatarSrc = httpsUrl(status?.avatar);
  return (
    <section className="border rounded-lg p-4 bg-white">
      <h3 className="text-sm font-semibold mb-3">Conexão LinkedIn</h3>
      {connected ? (
        <>
          <div className="flex gap-3 mb-3">
            {avatarSrc ? (
              <img
                src={avatarSrc}
                alt=""
                className="w-9 h-9 rounded-full object-cover"
              />
            ) : (
              <div className="w-9 h-9 rounded-full bg-[#0a66c2] text-white flex items-center justify-center text-sm font-semibold">
                {status?.name?.[0] ?? "?"}
              </div>
            )}
            <div className="text-sm font-medium leading-tight">{status?.name}</div>
          </div>
          <div className="text-xs mb-3">
            <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 mr-2 align-middle" />
            Conectado · expira em {status?.expiresInDays} dias
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              className="rounded border border-slate-300 px-3 py-1.5 text-xs hover:bg-slate-50"
              onClick={() => window.location.assign(startUrl())}
            >
              Reconectar
            </button>
          </div>
          <hr className="my-3 border-slate-200" />
          <div className="flex justify-between items-center mb-1">
            <span className="text-xs font-semibold">Modo Sandbox</span>
            <button
              type="button"
              aria-label="Toggle sandbox mode"
              aria-pressed={!!cfg?.forceDraftMode}
              onClick={() => toggle.mutate(!cfg?.forceDraftMode)}
              className={`w-8 h-[18px] rounded-full relative transition-colors ${cfg?.forceDraftMode ? "bg-amber-500" : "bg-slate-300"}`}
            >
              <span
                className={`block w-[14px] h-[14px] bg-white rounded-full absolute top-[2px] transition-all ${cfg?.forceDraftMode ? "left-[16px]" : "left-[2px]"}`}
              />
            </button>
          </div>
          <p className="text-[11px] text-slate-500">
            Quando ligado, posts saem como rascunho invisível no LinkedIn
          </p>
        </>
      ) : (
        <>
          <p className="text-xs text-slate-500 mb-3">
            Conecte sua conta LinkedIn pra publicar posts pelo dashboard.
          </p>
          <button
            type="button"
            className="rounded bg-slate-900 text-white px-4 py-2 text-sm font-medium"
            onClick={() => window.location.assign(startUrl())}
          >
            Conectar LinkedIn
          </button>
        </>
      )}
    </section>
  );
};
